/**
 * Wizard Management Page
 * Phase 3 - Consultant interface to manage wizard templates
 * 
 * Features:
 * - View all wizards (default + custom)
 * - Create new wizards
 * - Edit custom wizards
 * - Delete custom wizards
 * - Publish/unpublish wizards
 * - Preview wizards
 */

import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Plus, Search, Edit, Trash2, Eye, EyeOff, Wand2, Play,
  Settings, Zap, FileText, Users, MoreVertical, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { WizardTemplateBuilder } from './WizardTemplateBuilder';
import { DynamicWizard } from '../DynamicWizard';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { formatCategoryLabel } from '../../utils/normalizeAcronyms';

interface WizardTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  pages: any[];
  published: boolean;
  isDefault: boolean;
  allowSkip: boolean;
  showProgress: boolean;
  autoSave: boolean;
  placement: {
    autoLaunch: {
      enabled: boolean;
      trigger: string;
      categories: string[];
    };
    manualAccess: {
      showInWizardLibrary: boolean;
      showInCategoryActions: boolean;
      showInGridActions: boolean;
      showInCIDetail: boolean;
    };
    availableToRoles: string[];
  };
  createdAt: string;
  createdByName?: string;
  lastModified?: string;
}

interface WizardManagementPageProps {
  consultingFirmId: string;
  accessToken: string;
  userId: string;
  userName: string;
}

export function WizardManagementPage({
  consultingFirmId,
  accessToken,
  userId,
  userName
}: WizardManagementPageProps) {
  const [loading, setLoading] = useState(false);
  const [wizards, setWizards] = useState<WizardTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterType, setFilterType] = useState<'all' | 'default' | 'custom'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'unpublished'>('all');

  // Dialogs
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingWizardId, setEditingWizardId] = useState<string | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [wizardToDelete, setWizardToDelete] = useState<WizardTemplate | null>(null);

  useEffect(() => {
    loadWizards();
  }, []);

  async function loadWizards() {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/wizard-templates`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'x-consulting-firm-id': consultingFirmId
          }
        }
      );

      if (!response.ok) throw new Error('Failed to load wizards');

      const data = await response.json();
      setWizards(data.wizards || []);
    } catch (error) {
      console.error('Error loading wizards:', error);
      toast.error('Failed to load wizard templates');
    } finally {
      setLoading(false);
    }
  }

  async function handleTogglePublish(wizard: WizardTemplate) {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/wizard-templates/${wizard.id}/publish`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'x-consulting-firm-id': consultingFirmId,
            'x-user-id': userId,
            'x-user-name': userName,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ published: !wizard.published })
        }
      );

      if (!response.ok) throw new Error('Failed to update wizard');

      toast.success(`Wizard ${wizard.published ? 'unpublished' : 'published'} successfully`);
      loadWizards();
    } catch (error) {
      console.error('Error toggling publish status:', error);
      toast.error('Failed to update wizard');
    }
  }

  async function handleDeleteWizard() {
    if (!wizardToDelete) return;

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/wizard-templates/${wizardToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'x-consulting-firm-id': consultingFirmId
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete wizard');
      }

      toast.success('Wizard deleted successfully');
      setDeleteDialogOpen(false);
      setWizardToDelete(null);
      loadWizards();
    } catch (error: any) {
      console.error('Error deleting wizard:', error);
      toast.error(error.message || 'Failed to delete wizard');
    }
  }

  function handleEdit(wizard: WizardTemplate) {
    // All wizards can be edited - "default" just means it came from Pathkit
    setEditingWizardId(wizard.id);
    setBuilderOpen(true);
  }

  function handleViewOrEdit(wizard: WizardTemplate) {
    // Clicking the card opens edit mode
    setEditingWizardId(wizard.id);
    setBuilderOpen(true);
  }

  function handleCreateNew() {
    setEditingWizardId(undefined);
    setBuilderOpen(true);
  }

  function handleDuplicate(wizard: WizardTemplate) {
    // TODO: Implement duplicate functionality
    toast.info('Duplicate feature coming soon');
  }

  const categories = ['all', ...new Set(wizards.map(w => w.category))];

  const filteredWizards = wizards.filter(w => {
    if (searchQuery && 
        !w.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !w.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filterCategory !== 'all' && w.category !== filterCategory) {
      return false;
    }
    if (filterType === 'default' && !w.isDefault) {
      return false;
    }
    if (filterType === 'custom' && w.isDefault) {
      return false;
    }
    if (filterStatus === 'published' && !w.published) {
      return false;
    }
    if (filterStatus === 'unpublished' && w.published) {
      return false;
    }
    return true;
  });

  const stats = {
    total: wizards.length,
    default: wizards.filter(w => w.isDefault).length,
    custom: wizards.filter(w => !w.isDefault).length,
    published: wizards.filter(w => w.published).length,
    autoLaunch: wizards.filter(w => w.placement?.autoLaunch?.enabled).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading wizards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Wand2 className="w-8 h-8 text-indigo-600" />
              Wizard Templates
            </h1>
            <p className="text-gray-600 mt-1">
              Create and manage guided configuration wizards for your clients
            </p>
          </div>
          <Button onClick={handleCreateNew} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" />
            Create Wizard
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Wizards</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-600">Default</p>
            <p className="text-2xl font-bold text-blue-600">{stats.default}</p>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-600">Custom</p>
            <p className="text-2xl font-bold text-purple-600">{stats.custom}</p>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-600">Published</p>
            <p className="text-2xl font-bold text-green-600">{stats.published}</p>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-600">Auto-Launch</p>
            <p className="text-2xl font-bold text-amber-600">{stats.autoLaunch}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 mb-6">
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search wizards by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filter buttons */}
          <div className="flex items-center gap-6 flex-wrap">
            {/* Category */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Category:</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-1.5 border rounded-md text-sm"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All' : formatCategoryLabel(cat)}
                  </option>
                ))}
              </select>
            </div>

            {/* Type */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Type:</label>
              <div className="flex gap-2">
                {['all', 'default', 'custom'].map(type => (
                  <button
                    key={type}
                    onClick={() => setFilterType(type as any)}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                      filterType === type
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <div className="flex gap-2">
                {['all', 'published', 'unpublished'].map(status => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status as any)}
                    className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                      filterStatus === status
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wizards Grid */}
      {filteredWizards.length === 0 ? (
        <div className="bg-white border rounded-lg p-12 text-center">
          <Wand2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No wizards found</h3>
          <p className="text-gray-600 mb-6">
            {searchQuery || filterCategory !== 'all' || filterType !== 'all' || filterStatus !== 'all'
              ? 'Try adjusting your filters'
              : 'Get started by creating your first wizard template'}
          </p>
          {wizards.length === 0 && (
            <Button onClick={handleCreateNew} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Wizard
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredWizards.map(wizard => (
            <WizardTemplateCard
              key={wizard.id}
              wizard={wizard}
              onEdit={() => handleEdit(wizard)}
              onDelete={() => {
                setWizardToDelete(wizard);
                setDeleteDialogOpen(true);
              }}
              onTogglePublish={() => handleTogglePublish(wizard)}
              onDuplicate={() => handleDuplicate(wizard)}
              onViewOrEdit={() => handleViewOrEdit(wizard)}
            />
          ))}
        </div>
      )}

      {/* Results count */}
      {filteredWizards.length > 0 && (
        <div className="mt-6 text-sm text-gray-600 text-center">
          Showing {filteredWizards.length} of {wizards.length} wizard{wizards.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Wizard Builder Dialog */}
      <WizardTemplateBuilder
        open={builderOpen}
        onClose={() => {
          setBuilderOpen(false);
          setEditingWizardId(undefined);
        }}
        wizardId={editingWizardId}
        consultingFirmId={consultingFirmId}
        accessToken={accessToken}
        onSave={() => {
          loadWizards();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Wizard Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{wizardToDelete?.name}"? This action cannot be undone.
              {wizardToDelete?.published && (
                <span className="block mt-2 text-amber-600 font-medium">
                  This wizard is currently published and may be in use by clients.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteWizard}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Wizard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface WizardTemplateCardProps {
  wizard: WizardTemplate;
  onEdit: () => void;
  onDelete: () => void;
  onTogglePublish: () => void;
  onDuplicate: () => void;
  onViewOrEdit: () => void;
}

function WizardTemplateCard({
  wizard,
  onEdit,
  onDelete,
  onTogglePublish,
  onDuplicate,
  onViewOrEdit
}: WizardTemplateCardProps) {
  const totalQuestions = wizard.pages.reduce((sum, page) => sum + (page.questionIds?.length || 0), 0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div 
      className="bg-white border rounded-lg hover:border-indigo-300 transition-all hover:shadow-md p-4 cursor-pointer relative"
      onClick={onViewOrEdit}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="font-semibold text-lg">{wizard.name}</h3>
            {wizard.isDefault && (
              <Badge variant="secondary" className="text-xs">
                Default
              </Badge>
            )}
            {wizard.published ? (
              <Badge className="bg-green-100 text-green-700 text-xs">
                <Eye className="w-3 h-3 mr-1" />
                Published
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs">
                <EyeOff className="w-3 h-3 mr-1" />
                Draft
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-600 line-clamp-2">
            {wizard.description || 'No description'}
          </p>
        </div>

        {/* Actions menu - Simple CSS dropdown */}
        <div className="relative ml-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
          >
            <MoreVertical className="w-4 h-4" />
          </Button>

          {isMenuOpen && (
            <>
              {/* Backdrop to close menu */}
              <div 
                className="fixed inset-0 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(false);
                }}
              />
              
              {/* Dropdown menu */}
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border rounded-lg shadow-lg z-20 py-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(false);
                    onEdit();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 transition-colors text-left"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(false);
                    onDuplicate();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 transition-colors text-left"
                >
                  <Copy className="w-4 h-4" />
                  Duplicate
                </button>
                <div className="h-px bg-gray-200 my-1" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(false);
                    onTogglePublish();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 transition-colors text-left"
                >
                  {wizard.published ? (
                    <>
                      <EyeOff className="w-4 h-4" />
                      Unpublish
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      Publish
                    </>
                  )}
                </button>
                {!wizard.isDefault && (
                  <>
                    <div className="h-px bg-gray-200 my-1" />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsMenuOpen(false);
                        onDelete();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-red-50 text-red-600 transition-colors text-left"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mb-3">
        <div className="flex items-center gap-1">
          <FileText className="w-3 h-3" />
          <span>{wizard.pages.length} page{wizard.pages.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-1">
          <Settings className="w-3 h-3" />
          <span>{totalQuestions} question{totalQuestions !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-1">
          <Users className="w-3 h-3" />
          <span className="capitalize">
            {wizard.placement.availableToRoles.includes('both')
              ? 'All'
              : wizard.placement.availableToRoles.join(', ')}
          </span>
        </div>
        {wizard.placement.autoLaunch?.enabled && (
          <div className="flex items-center gap-1 text-amber-600">
            <Zap className="w-3 h-3" />
            <span>Auto-launch</span>
          </div>
        )}
      </div>

      {/* Category badge */}
      <div className="mb-3">
        <span className="inline-block px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full">
          {formatCategoryLabel(wizard.category)}
        </span>
      </div>

      {/* Footer */}
      {wizard.createdByName && (
        <div className="pt-3 border-t text-xs text-gray-500">
          Created by {wizard.createdByName}
        </div>
      )}
    </div>
  );
}